/**
 * Jeu de données démo enrichi — tenant demo-hospitol uniquement.
 * Complète les seeds minimaux des stores (patients, admissions, labo, etc.)
 * pour une démonstration réaliste d'un centre hospitalier actif.
 */
import { loadStoredSession } from "@itaas/core/auth/auth.storage";
import { insurerCodeToOrgId } from "@itaas/insurance-contract";
import {
  createAdmission,
  listAdmissions,
  orientAdmission,
  updateAdmissionStatus,
  updateAdmissionTriage,
  updateEmergencyTriage,
} from "@/core/admissions/admission-store";
import { createAppointment, listAppointments } from "@/core/appointments/appointment-store";
import {
  createConsultation,
  listConsultations,
  setConsultationStatus,
} from "@/core/clinical/clinical-store";
import { IAM_TENANT_SLUG } from "@/core/config";
import { isDemoModeEnabled } from "@/core/demo/is-demo-mode-enabled";
import {
  assignBedToStay,
  createStayFromAdmission,
  listActiveStays,
  listHospitalStays,
  patchStayBedAssignedAt,
} from "@/core/hospitalization/hospitalization-store";
import {
  completeSurgeryCase,
  createSurgeryCase,
  listSurgeryCases,
} from "@/core/surgery/surgery-case-store";
import {
  createImagingExam,
  listImagingExams,
  saveImagingReport,
  updateImagingExamStatus,
} from "@/core/imaging/imaging-store";
import { createCoverageCase, listCoverageCases, reviewCoverageCase } from "@/core/insurance/coverage-case-store";
import { listInsurerAgents, listInsurers } from "@/core/insurance/insurer-store";
import type { HospitalPartnership } from "@/core/insurance/partnership-store";
import { getActivePartnershipForInsurer } from "@/core/insurance/partnership-store";
import {
  createLabOrder,
  listLabOrders,
  saveLabResult,
  updateLabOrderStatus,
} from "@/core/laboratory/lab-store";
import { labResultPresetForTest } from "@/core/laboratory/types";
import { createPatient, listPatients, updatePatient } from "@/core/patients/patient-store";
import type { Patient, PatientInput } from "@/core/patients/types";
import { enqueueTicket, listQueueTickets } from "@/core/queue/service-queue-store";
import { creditTreasuryAccount, listTreasuryMovements } from "@/core/treasury/treasury-store";
import { getStorageItem, removeStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import {
  patchAdmissionTimestamps,
  seedDemoPresentationReady,
} from "./demo-seed-utils";

export const DEMO_RICH_TAG = "[DÉMO-RICH]";
export const DEMO_RICH_VERSION = "rich-v4";
const SEED_KEY = "hospitol.demoRichSeeded";
const TREASURY_SEED_KEY = "hospitol.treasury.demoSeeded";
const PARTNERSHIPS_KEY = "hospitol.insurancePartnerships";

const FIRST_NAMES = [
  "Grace", "Patrick", "Chantal", "Emmanuel", "Naomi", "Fabrice", "Divine", "Esther", "Michel",
  "Rebecca", "Samuel", "Sandra", "Jonathan", "Prisca", "Benjamin", "Ruth", "Kevin", "Clotilde",
  "Gédéon", "Amina", "Junior", "Sarah", "David", "Joseph", "Rachel", "Pauline", "Hervé",
] as const;

const LAST_NAMES = [
  "Tshilombo", "Mbuyi", "Ngoy", "Kasongo", "Mutombo", "Kapinga", "Mwanza", "Mpunga", "Lukau",
  "Tshiala", "Kazadi", "Kalala", "Banza", "Mukuna", "Nsimba", "Kabeya", "Mulumba", "Tshisekedi",
  "Kabongo", "Ilunga", "Mwamba", "Luboya", "Kalonji", "Mukuna", "Banza", "Kapinga",
] as const;

const KINSHASA_STREETS = [
  "12 Av. Lumumba, Kinshasa",
  "45 Q. Gombe, Kinshasa",
  "78 Bd du 30 Juin, Kinshasa",
  "3 Av. Kasa-Vubu, Kinshasa",
  "22 Q. Bandal, Kinshasa",
  "9 Rue Kabambare, Kinshasa",
  "56 Q. Lemba, Kinshasa",
  "14 Av. Tombalbaye, Kinshasa",
];

const LUBUMBASHI_STREETS = [
  "8 Rue Commerce, Lubumbashi",
  "15 Av. Mobutu, Lubumbashi",
  "33 Q. Kenya, Lubumbashi",
];

function richMotif(label: string): string {
  return `${DEMO_RICH_TAG} ${label}`;
}

function findPatient(lastName: string): Patient | undefined {
  return listPatients().find((p) => p.lastName === lastName);
}

function hasMotifInAdmissions(motif: string): boolean {
  return listAdmissions().some((a) => a.motif.includes(motif));
}

function hasMotifInConsultations(motif: string): boolean {
  return listConsultations().some((c) => c.motif.includes(motif));
}

function hasLabTest(testName: string, patientId: string): boolean {
  return listLabOrders().some((o) => o.patientId === patientId && o.testName === testName);
}

function hasImagingExam(examType: string, patientId: string): boolean {
  return listImagingExams().some((e) => e.patientId === patientId && e.examType === examType);
}

function hasQueueMotif(motif: string): boolean {
  return listQueueTickets().some((t) => t.motif.includes(motif));
}

function phoneForIndex(i: number): string {
  const base = 810000000 + i * 17_371;
  const s = String(base).padStart(9, "0");
  return `+243 ${s.slice(0, 2)} ${s.slice(2, 5)} ${s.slice(5)}`;
}

function birthDateForAge(age: number): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - age);
  return d.toISOString().slice(0, 10);
}

function buildRichPatients(): PatientInput[] {
  const seen = new Set(["Jean Mukendi", "Marie Kabila", "Paul Ilunga"]);
  const patients: PatientInput[] = [];

  for (let i = 0; i < 35; i++) {
    const firstName = FIRST_NAMES[i % FIRST_NAMES.length]!;
    const lastName = LAST_NAMES[i % LAST_NAMES.length]!;
    const key = `${firstName} ${lastName}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const gender = i % 3 === 0 ? "female" : i % 3 === 1 ? "male" : "other";
    const address =
      i % 4 === 0
        ? LUBUMBASHI_STREETS[i % LUBUMBASHI_STREETS.length]!
        : KINSHASA_STREETS[i % KINSHASA_STREETS.length]!;
    patients.push({
      firstName,
      lastName,
      gender,
      patientType: i % 7 === 0 ? "inpatient" : "outpatient",
      phone: phoneForIndex(i + 10),
      address,
      birthDate: birthDateForAge(18 + (i % 55)),
      notes: i % 5 === 0 ? "Suivi chronique — démo" : "",
    });
  }
  return patients;
}

function seedRichPatients(): void {
  for (const input of buildRichPatients()) {
    const exists = listPatients().some(
      (p) => p.firstName === input.firstName && p.lastName === input.lastName,
    );
    if (exists) continue;
    createPatient(input);
  }

  const mutuelle = listInsurers(true).find((i) => i.code === "MUT-SALAMA");
  const asr = listInsurers(true).find((i) => i.code === "ASR-VIE");
  const agent = mutuelle ? listInsurerAgents(mutuelle.id)[0] : null;

  const insuredTargets: Array<{ lastName: string; insurerId: string; name: string; member: string }> =
    [
      { lastName: "Tshilombo", insurerId: mutuelle?.id ?? "", name: mutuelle?.name ?? "", member: "MB-2026-0142" },
      { lastName: "Kasongo", insurerId: asr?.id ?? "", name: asr?.name ?? "", member: "MB-ASR-0881" },
      { lastName: "Mutombo", insurerId: mutuelle?.id ?? "", name: mutuelle?.name ?? "", member: "MB-2026-0198" },
      { lastName: "Mpunga", insurerId: mutuelle?.id ?? "", name: mutuelle?.name ?? "", member: "MB-2026-0211" },
      { lastName: "Tshiala", insurerId: asr?.id ?? "", name: asr?.name ?? "", member: "MB-ASR-0902" },
      { lastName: "Kabeya", insurerId: mutuelle?.id ?? "", name: mutuelle?.name ?? "", member: "MB-2026-0255" },
    ];

  for (const target of insuredTargets) {
    if (!target.insurerId) continue;
    const patient = findPatient(target.lastName);
    if (!patient || patient.coverageType === "insurer") continue;
    updatePatient(patient.id, {
      firstName: patient.firstName,
      lastName: patient.lastName,
      gender: patient.gender,
      patientType: patient.patientType,
      phone: patient.phone,
      address: patient.address,
      birthDate: patient.birthDate,
      coverageType: "insurer",
      coverageName: target.name,
      insurerId: target.insurerId,
      insurerAgentId: agent?.id ?? null,
      memberNumber: target.member,
      policyNumber: "POL-SALAMA-2026",
    });
  }
}

function ensurePartnership(insurerId: string, insurerCode: string): void {
  if (getActivePartnershipForInsurer(insurerId)) return;
  const session = loadStoredSession();
  const now = new Date().toISOString();
  const partnership: HospitalPartnership = {
    id: `part-rich-${insurerCode.toLowerCase()}`,
    insurerOrgId: insurerCodeToOrgId(insurerCode),
    insurerId,
    hospitalTenantId: session?.tenant?.id ?? "tenant-demo-hospitol",
    hospitalTenantSlug: session?.tenant?.slug ?? IAM_TENANT_SLUG,
    hospitalName: session?.tenant?.name ?? "Centre Médical Salama",
    status: "active",
    requestId: "demo-rich-seed",
    approvedAt: now,
    createdAt: now,
    approvalCode: "DEMO-RICH-APPROVED",
  };
  const raw = getStorageItem(PARTNERSHIPS_KEY);
  const list: HospitalPartnership[] = raw ? (JSON.parse(raw) as HospitalPartnership[]) : [];
  list.unshift(partnership);
  setStorageItem(PARTNERSHIPS_KEY, JSON.stringify(list));
  window.dispatchEvent(new CustomEvent("hospitol:insurance-partnerships"));
}

function seedRichPartnerships(): void {
  for (const insurer of listInsurers(true)) {
    ensurePartnership(insurer.id, insurer.code);
  }
}

function seedRichAdmissions(): void {
  const scenarios: Array<{
    lastName: string;
    channel: "admission" | "emergency";
    motif: string;
    status: "waiting" | "in_progress" | "oriented" | "pending_billing";
    triage?: "red" | "yellow" | "green";
  }> = [
    { lastName: "Tshilombo", channel: "admission", motif: "Contrôle diabète", status: "waiting" },
    { lastName: "Mbuyi", channel: "admission", motif: "Certificat médical", status: "in_progress" },
    { lastName: "Ngoy", channel: "admission", motif: "Douleur lombaire", status: "waiting" },
    { lastName: "Kasongo", channel: "admission", motif: "Renouvellement ordonnance", status: "oriented" },
    { lastName: "Mutombo", channel: "admission", motif: "Consultation pédiatrique", status: "in_progress" },
    { lastName: "Kapinga", channel: "admission", motif: "Bilan pré-opératoire", status: "waiting" },
    { lastName: "Mwanza", channel: "emergency", motif: "Crise hypertensive", status: "in_progress", triage: "yellow" },
    { lastName: "Banza", channel: "emergency", motif: "Accouchement imminent", status: "in_progress", triage: "red" },
    { lastName: "Nsimba", channel: "emergency", motif: "Entorse cheville", status: "waiting", triage: "green" },
    { lastName: "Kabeya", channel: "emergency", motif: "Paludisme suspecté", status: "in_progress", triage: "yellow" },
    { lastName: "Mulumba", channel: "admission", motif: "Visite de contrôle post-op", status: "oriented" },
    { lastName: "Tshisekedi", channel: "admission", motif: "Vaccination adulte", status: "waiting" },
    { lastName: "Ilunga", channel: "admission", motif: "Consultation cardiologie — proforma requise", status: "pending_billing" },
  ];

  for (const scenario of scenarios) {
    const motif = richMotif(scenario.motif);
    if (hasMotifInAdmissions(motif)) continue;
    const patient = findPatient(scenario.lastName);
    if (!patient) continue;

    const adm = createAdmission({
      patientId: patient.id,
      channel: scenario.channel,
      motif,
      triageLevel: scenario.triage ?? null,
    });

    if (scenario.channel === "emergency" && scenario.triage) {
      updateAdmissionTriage(adm.id, scenario.triage);
      updateEmergencyTriage(adm.id, {
        triageLevel: scenario.triage,
        glasgowScore: scenario.triage === "red" ? 14 : 15,
        allergiesNote: scenario.triage === "red" ? "Pénicilline" : "",
        triageNotes: "Triage démo — " + scenario.motif,
      });
    }

    if (scenario.status !== "waiting") {
      updateAdmissionStatus(adm.id, scenario.status);
    }
  }
}

function seedRichConsultations(): void {
  const scenarios: Array<{
    lastName: string;
    motif: string;
    diagnostic: string;
    status: "open" | "closed";
    vitals?: {
      bloodPressureSystolic: number;
      bloodPressureDiastolic: number;
      heartRate: number;
      temperatureC: number;
    };
  }> = [
    {
      lastName: "Mbuyi",
      motif: "Lombalgie chronique",
      diagnostic: "Lombalgie mécanique — repos relatif",
      status: "open",
      vitals: { bloodPressureSystolic: 128, bloodPressureDiastolic: 82, heartRate: 76, temperatureC: 36.8 },
    },
    {
      lastName: "Ngoy",
      motif: "Céphalées récurrentes",
      diagnostic: "Céphalée de tension",
      status: "open",
      vitals: { bloodPressureSystolic: 122, bloodPressureDiastolic: 78, heartRate: 72, temperatureC: 36.5 },
    },
    {
      lastName: "Kapinga",
      motif: "Préparation chirurgie",
      diagnostic: "Appendicite suspectée — bilan pré-op",
      status: "open",
    },
    {
      lastName: "Lukau",
      motif: "Toux productive",
      diagnostic: "Bronchite aiguë",
      status: "closed",
      vitals: { bloodPressureSystolic: 120, bloodPressureDiastolic: 80, heartRate: 84, temperatureC: 37.4 },
    },
    {
      lastName: "Kazadi",
      motif: "Suivi HTA",
      diagnostic: "Hypertension stabilisée sous traitement",
      status: "closed",
      vitals: { bloodPressureSystolic: 138, bloodPressureDiastolic: 88, heartRate: 78, temperatureC: 36.6 },
    },
    {
      lastName: "Kalala",
      motif: "Douleurs thoraciques atypiques",
      diagnostic: "Douleur musculo-squelettique — ECG normal",
      status: "closed",
    },
    {
      lastName: "Mukuna",
      motif: "Fièvre chez l'enfant",
      diagnostic: "Infection virale probable",
      status: "open",
      vitals: { bloodPressureSystolic: 100, bloodPressureDiastolic: 65, heartRate: 110, temperatureC: 38.9 },
    },
    {
      lastName: "Tshiala",
      motif: "Suivi grossesse T2",
      diagnostic: "Grossesse évolutive normale",
      status: "open",
    },
  ];

  for (const scenario of scenarios) {
    const motif = richMotif(scenario.motif);
    if (hasMotifInConsultations(motif)) continue;
    const patient = findPatient(scenario.lastName);
    if (!patient) continue;

    const consultation = createConsultation({
      patientId: patient.id,
      motif,
      diagnostic: scenario.diagnostic,
      acts: "Consultation — démo enrichie",
      vitals: scenario.vitals ?? null,
    });

    if (scenario.status === "closed") {
      setConsultationStatus(consultation.id, "closed");
    }
  }
}

function seedRichLabOrders(): void {
  const mutuelle = listInsurers(true).find((i) => i.code === "MUT-SALAMA");

  const scenarios: Array<{
    lastName: string;
    testName: string;
    status: "requested" | "in_progress" | "completed" | "pending_insurer";
    notes: string;
    insured?: boolean;
  }> = [
    { lastName: "Lukau", testName: "Test paludisme (TDR)", status: "completed", notes: "Fièvre — dépistage" },
    { lastName: "Kazadi", testName: "Créatininémie", status: "completed", notes: "Suivi néphrologique léger" },
    { lastName: "Mbuyi", testName: "Glycémie à jeun", status: "in_progress", notes: "Bilan métabolique" },
    { lastName: "Tshiala", testName: "Bilan hépatique", status: "requested", notes: "Grossesse — contrôle" },
    { lastName: "Kabeya", testName: "NFS (Numération formule sanguine)", status: "in_progress", notes: "Fièvre — paludisme suspecté", insured: true },
    { lastName: "Kapinga", testName: "Groupe sanguin + Rhésus", status: "requested", notes: "Pré-opératoire" },
    { lastName: "Mutombo", testName: "Ionogramme sanguin", status: "completed", notes: "Déshydratation légère" },
    { lastName: "Kasongo", testName: "Urée / créatinine", status: "requested", notes: "Bilan rénal — assuré", insured: true },
    { lastName: "Mpunga", testName: "Dosage FSH (fertilité)", status: "pending_insurer", notes: "Accord assureur requis", insured: true },
  ];

  for (const scenario of scenarios) {
    const patient = findPatient(scenario.lastName);
    if (!patient || hasLabTest(scenario.testName, patient.id)) continue;

    const consultation = listConsultations().find((c) => c.patientId === patient.id);
    const { order } = createLabOrder({
      patientId: patient.id,
      prescriberId: "demo-prac-mukendi",
      consultationId: consultation?.id ?? null,
      testName: scenario.testName,
      clinicalNotes: richMotif(scenario.notes),
    });

    if (scenario.status !== "requested" && scenario.status !== "pending_insurer") {
      updateLabOrderStatus(order.id, scenario.status);
    }

    if (scenario.status === "completed") {
      const preset = labResultPresetForTest(scenario.testName);
      saveLabResult(order.id, {
        resultLines: preset.map((line) => ({
          parameter: line.parameter,
          value: demoLabValue(line.parameter),
          unit: line.unit,
          referenceRange: line.referenceRange,
          flag: "normal" as const,
        })),
        resultText: "",
      });
    }

    if (scenario.insured && mutuelle) {
      const cc = createCoverageCase({
        insurerId: mutuelle.id,
        patientId: patient.id,
        labOrderId: order.id,
        kind: "laboratory",
        label: `${scenario.testName} — démo`,
        amount: scenario.testName.includes("NFS") ? 18_000 : 12_000,
        memberNumber: patient.memberNumber,
        policyNumber: patient.policyNumber,
      });
      if (cc && scenario.status === "completed") {
        reviewCoverageCase(cc.id, "approved", "Démo seed");
      }
    }
  }
}

function seedRichImagingExams(): void {
  const asr = listInsurers(true).find((i) => i.code === "ASR-VIE");
  const now = Date.now();

  const scenarios: Array<{
    lastName: string;
    examType: string;
    bodyPart: string;
    indication: string;
    status: "scheduled" | "in_progress" | "completed";
    offsetHours: number;
    insured?: boolean;
  }> = [
    { lastName: "Kalala", examType: "Échographie", bodyPart: "Abdomen", indication: "Douleurs épigastriques", status: "scheduled", offsetHours: 4 },
    { lastName: "Ngoy", examType: "Radiographie", bodyPart: "Rachis lombaire", indication: "Lombalgie", status: "scheduled", offsetHours: 6 },
    { lastName: "Kapinga", examType: "Scanner", bodyPart: "Abdomen", indication: "Appendicite suspectée", status: "in_progress", offsetHours: 2 },
    { lastName: "Tshiala", examType: "Échographie", bodyPart: "Obstétrique", indication: "Suivi grossesse 22 SA", status: "completed", offsetHours: -3 },
    { lastName: "Kasongo", examType: "IRM", bodyPart: "Genou", indication: "Douleur chronique genou D", status: "scheduled", offsetHours: 48, insured: true },
    { lastName: "Mbuyi", examType: "Radiographie", bodyPart: "Thorax", indication: "Toux persistante", status: "completed", offsetHours: -24 },
  ];

  for (const scenario of scenarios) {
    const patient = findPatient(scenario.lastName);
    if (!patient || hasImagingExam(scenario.examType, patient.id)) continue;

    const consultation = listConsultations().find((c) => c.patientId === patient.id);
    const { exam } = createImagingExam({
      patientId: patient.id,
      prescriberId: "demo-prac-kabila",
      consultationId: consultation?.id ?? null,
      examType: scenario.examType,
      bodyPart: scenario.bodyPart,
      clinicalIndication: richMotif(scenario.indication),
      scheduledAt: new Date(now + scenario.offsetHours * 3_600_000).toISOString(),
    });

    if (scenario.status === "in_progress") {
      updateImagingExamStatus(exam.id, "in_progress");
    }

    if (scenario.status === "completed") {
      saveImagingReport(exam.id, {
        technique: scenario.examType + " standard — démo",
        findings:
          scenario.bodyPart === "Obstétrique"
            ? "Fœtus unique, activité cardiaque présente, liquide amniotique normal."
            : "Pas d'anomalie significative détectée sur les coupes analysées.",
        conclusion: "Examen dans les limites de la normale pour la démo.",
      });
    }

    if (scenario.insured && asr) {
      const cc = createCoverageCase({
        insurerId: asr.id,
        patientId: patient.id,
        imagingExamId: exam.id,
        kind: "imaging",
        label: `${scenario.examType} ${scenario.bodyPart}`,
        amount: 185_000,
        memberNumber: patient.memberNumber,
        policyNumber: patient.policyNumber,
      });
      if (cc && scenario.status !== "scheduled") {
        reviewCoverageCase(cc.id, "approved", "Démo seed");
      } else if (cc) {
        // laisser en pending pour la file assurance
      }
    }
  }
}

function demoLabValue(parameter: string): string {
  const lower = parameter.toLowerCase();
  if (lower.includes("paludisme") || lower.includes("tdr")) return "Négatif";
  if (lower.includes("glycémie")) return "0.92";
  if (lower.includes("créatinine") || lower.includes("creatinine")) return "9.8";
  if (lower.includes("dfg")) return "95";
  if (lower.includes("hémoglobine")) return "13.4";
  if (lower.includes("hématocrite")) return "40";
  if (lower.includes("leucocytes")) return "7 200";
  if (lower.includes("plaquettes")) return "245 000";
  if (lower.includes("sodium")) return "140";
  if (lower.includes("potassium")) return "4.1";
  return "Normal";
}

function seedRichAppointments(): void {
  const units = [
    "Consultation générale",
    "Cardiologie",
    "Pédiatrie",
    "Gynécologie",
    "Dermatologie",
    "ORL",
    "Ophtalmologie",
  ];
  const practitioners = ["demo-prac-mukendi", "demo-prac-kabila", "demo-prac-ilunga"];

  const patients = listPatients().filter((p) => !["Mukendi", "Kabila", "Ilunga"].includes(p.lastName));
  const base = new Date();
  base.setHours(8, 0, 0, 0);

  for (let i = 0; i < Math.min(14, patients.length); i++) {
    const patient = patients[i]!;
    const dayOffset = i % 10;
    const scheduled = new Date(base);
    scheduled.setDate(scheduled.getDate() + dayOffset);
    scheduled.setHours(8 + (i % 6), (i % 2) * 30, 0, 0);

    const motif = richMotif(
      [
        "Contrôle tension",
        "Suivi traitement",
        "Bilan annuel",
        "Consultation préventive",
        "Douleurs articulaires",
        "Suivi post-hospitalisation",
        "Certificat sport",
      ][i % 7]!,
    );

    if (listAppointments().some((a) => a.patientId === patient.id && a.motif === motif)) continue;

    createAppointment({
      patientId: patient.id,
      scheduledAt: scheduled.toISOString(),
      durationMinutes: i % 3 === 0 ? 45 : 30,
      serviceUnit: units[i % units.length]!,
      motif,
      practitionerId: practitioners[i % practitioners.length]!,
      status: i % 4 === 0 ? "confirmed" : "scheduled",
    });
  }
}

function seedRichQueueTickets(): void {
  const scenarios: Array<{
    lastName: string;
    serviceId: "clinical" | "laboratory" | "imaging" | "admission";
    unitName: string;
    motif: string;
    priority: number;
  }> = [
    { lastName: "Mbuyi", serviceId: "clinical", unitName: "Médecine générale", motif: "Lombalgie", priority: 10 },
    { lastName: "Ngoy", serviceId: "clinical", unitName: "Médecine générale", motif: "Céphalées", priority: 12 },
    { lastName: "Mukuna", serviceId: "clinical", unitName: "Pédiatrie", motif: "Fièvre enfant", priority: 8 },
    { lastName: "Kabeya", serviceId: "laboratory", unitName: "Laboratoire", motif: "Prélèvement NFS", priority: 10 },
    { lastName: "Kapinga", serviceId: "laboratory", unitName: "Laboratoire", motif: "Groupe sanguin", priority: 15 },
    { lastName: "Kalala", serviceId: "imaging", unitName: "Imagerie", motif: "Échographie abdomen", priority: 12 },
    { lastName: "Tshiala", serviceId: "imaging", unitName: "Imagerie", motif: "Écho obstétricale", priority: 9 },
    { lastName: "Tshilombo", serviceId: "admission", unitName: "Accueil", motif: "Enregistrement admission", priority: 20 },
    { lastName: "Kasongo", serviceId: "clinical", unitName: "Cardiologie", motif: "Suivi cardiologique", priority: 14 },
    { lastName: "Mutombo", serviceId: "clinical", unitName: "Pédiatrie", motif: "Consultation enfant", priority: 11 },
  ];

  for (const scenario of scenarios) {
    const motif = richMotif(scenario.motif);
    if (hasQueueMotif(motif)) continue;
    const patient = findPatient(scenario.lastName);
    if (!patient) continue;

    enqueueTicket({
      patientId: patient.id,
      serviceId: scenario.serviceId,
      unitName: scenario.unitName,
      motif,
      priority: scenario.priority,
    });
  }
}

function seedRichHospitalization(): void {
  if (listActiveStays().length >= 2) return;

  const scenarios: Array<{ lastName: string; motif: string; bedId: string; wardName: string }> = [
    { lastName: "Mpunga", motif: "Pneumonie communautaire", bedId: "bed-201a", wardName: "Médecine A" },
    { lastName: "Kabila", motif: "Surveillance post-partum", bedId: "bed-101a", wardName: "Maternité" },
  ];

  for (const scenario of scenarios) {
    const patient = findPatient(scenario.lastName);
    if (!patient) continue;

    const motif = richMotif(scenario.motif);
    let admission = listAdmissions().find((a) => a.patientId === patient.id && a.motif.includes(motif));
    if (!admission) {
      admission = createAdmission({
        patientId: patient.id,
        channel: "admission",
        motif,
      });
      updateAdmissionStatus(admission.id, "oriented");
    }

    const existingStay = listHospitalStays().find(
      (s) => s.admissionId === admission!.id && s.status !== "discharged",
    );
    if (existingStay?.bedId) continue;

    const stay = existingStay ?? createStayFromAdmission({
      patientId: patient.id,
      admissionId: admission.id,
      motif: scenario.motif,
      wardName: scenario.wardName,
    });

    if (!stay.bedId) {
      try {
        const assigned = assignBedToStay(stay.id, scenario.bedId);
        const daysAgo = scenario.bedId === "bed-201a" ? 6 : 3;
        const assignedAt = new Date();
        assignedAt.setDate(assignedAt.getDate() - daysAgo);
        const patched = patchStayBedAssignedAt(assigned.id, assignedAt.toISOString());
        if (patched) {
          void import("@/core/billing/extended-hospital-billing").then(
            ({ syncRecurringHospitalizationBillsForStay }) =>
              syncRecurringHospitalizationBillsForStay(patched),
          );
        }
      } catch {
        // lit déjà occupé en démo partielle
      }
    }
  }
}

function seedRichTreasury(): void {
  if (getStorageItem(TREASURY_SEED_KEY)) return;
  if (listTreasuryMovements().length > 0) {
    setStorageItem(TREASURY_SEED_KEY, DEMO_RICH_VERSION);
    return;
  }

  creditTreasuryAccount({
    accountId: "tr-ca-main",
    amount: 2_450_000,
    label: "Ouverture caisse — recettes veille",
    paymentMethod: "cash",
    reference: "DEMO-OPEN",
  });
  creditTreasuryAccount({
    accountId: "tr-mm-mpesa",
    amount: 890_000,
    label: "Encaissements Mobile Money",
    paymentMethod: "mobile_money",
    reference: "DEMO-MPESA",
  });
  creditTreasuryAccount({
    accountId: "tr-mm-orange",
    amount: 620_000,
    label: "Encaissements Orange Money",
    paymentMethod: "mobile_money",
    reference: "DEMO-ORANGE",
  });
  creditTreasuryAccount({
    accountId: "tr-bk-rawbank",
    amount: 15_800_000,
    label: "Virement assureurs — tiers payant",
    paymentMethod: "bank_transfer",
    reference: "DEMO-BANK",
  });

  setStorageItem(TREASURY_SEED_KEY, DEMO_RICH_VERSION);
}

function seedRichTodayAdmissions(): void {
  const todayIds = listAdmissions()
    .filter((a) => {
      const d = new Date(a.createdAt);
      const now = new Date();
      return (
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate()
      );
    })
    .map((a) => a.id);

  if (todayIds.length >= 8) return;

  const patients = listPatients();
  const createdIds: string[] = [];
  const motifs = [
    "Consultation du jour",
    "Contrôle tension",
    "Douleur thoracique légère",
    "Fièvre enfant",
    "Renouvellement traitement",
    "Certificat médical",
    "Urgence — entorse",
    "Suivi post-opératoire",
  ];

  for (let i = 0; i < motifs.length && createdIds.length + todayIds.length < 10; i++) {
    const motif = richMotif(motifs[i]!);
    if (hasMotifInAdmissions(motif)) continue;
    const patient = patients[(i + 3) % patients.length];
    if (!patient) continue;
    const adm = createAdmission({
      patientId: patient.id,
      channel: i % 5 === 0 ? "emergency" : "admission",
      motif,
      triageLevel: i % 5 === 0 ? "yellow" : null,
    });
    if (i % 3 === 0) updateAdmissionStatus(adm.id, "in_progress");
    createdIds.push(adm.id);
  }

  patchAdmissionTimestamps([...todayIds, ...createdIds], new Date(), 7);
}

function seedRichCoverageCases(): void {
  const mutuelle = listInsurers(true).find((i) => i.code === "MUT-SALAMA");
  if (!mutuelle) return;

  const pendingConsultation = listConsultations().find(
    (c) => c.motif.includes(DEMO_RICH_TAG) && c.status === "open",
  );
  if (pendingConsultation) {
    const patient = listPatients().find((p) => p.id === pendingConsultation.patientId);
    const exists = listCoverageCases().some(
      (cc) => cc.patientId === pendingConsultation.patientId && cc.kind === "consultation" && cc.status === "pending",
    );
    if (patient?.insurerId && !exists) {
      createCoverageCase({
        insurerId: mutuelle.id,
        patientId: patient.id,
        kind: "consultation",
        label: "Consultation spécialisée — accord préalable",
        amount: 35_000,
        memberNumber: patient.memberNumber,
        policyNumber: patient.policyNumber,
      });
    }
  }

  const rejectedPatient = findPatient("Nsimba");
  if (rejectedPatient) {
    const exists = listCoverageCases().some(
      (cc) => cc.patientId === rejectedPatient.id && cc.status === "rejected",
    );
    if (!exists) {
      const cc = createCoverageCase({
        insurerId: mutuelle.id,
        patientId: rejectedPatient.id,
        kind: "consultation",
        label: "IRM genou — hors nomenclature",
        amount: 420_000,
        memberNumber: "MB-2026-0999",
        policyNumber: "POL-SALAMA-2026",
      });
      if (cc) reviewCoverageCase(cc.id, "rejected", "Démo seed", "Acte non couvert par le contrat démo");
    }
  }
}

function seedRichSurgeryCases(): void {
  if (listSurgeryCases().length > 0) return;

  const patient = findPatient("Kapinga");
  if (!patient) return;

  const motif = richMotif("Bilan pré-opératoire");
  let admission = listAdmissions().find(
    (a) => a.patientId === patient.id && a.motif.includes(motif),
  );
  if (!admission) return;

  if (admission.status !== "oriented") {
    updateAdmissionStatus(admission.id, "in_progress");
    orientAdmission(admission.id, ["Chirurgie / bloc"]);
    admission = listAdmissions().find((a) => a.id === admission!.id) ?? admission;
  }

  const surgeryCase = createSurgeryCase({
    admissionId: admission.id,
    procedureName: "Appendicectomie",
    surgeonName: "Dr. Mukendi",
    durationMinutes: 75,
    procedureFee: 520_000,
    operativeNotes: "Appendicectomie par laparotomie — démo bloc opératoire.",
  });

  void completeSurgeryCase(surgeryCase.id);
}

/**
 * Alimente le tenant demo-hospitol avec un volume de données cohérent pour les démos.
 * Idempotent — peut être relancé sans doublon (marqueur [DÉMO-RICH]).
 */
export function seedDemoHospitolRich(): void {
  if (!isDemoModeEnabled()) return;
  const tenantSlug = loadStoredSession()?.tenant?.slug ?? IAM_TENANT_SLUG;
  if (tenantSlug !== "demo-hospitol") return;
  if (getStorageItem(SEED_KEY) === DEMO_RICH_VERSION) return;

  listPatients();
  listInsurers();

  seedDemoPresentationReady();
  seedRichPatients();
  seedRichPartnerships();
  seedRichAdmissions();
  seedRichTodayAdmissions();
  seedRichConsultations();
  seedRichLabOrders();
  seedRichImagingExams();
  seedRichAppointments();
  seedRichQueueTickets();
  seedRichHospitalization();
  seedRichSurgeryCases();
  seedRichTreasury();
  seedRichCoverageCases();

  setStorageItem(SEED_KEY, DEMO_RICH_VERSION);

  console.info(
    "[Hospitol démo] Seed enrichi prêt —",
    listPatients().length,
    "patients ·",
    listAdmissions().length,
    "admissions ·",
    listConsultations().length,
    "consultations ·",
    listLabOrders().length,
    "labo ·",
    listImagingExams().length,
    "imagerie",
  );
}

/** Force le rechargement du seed enrichi (efface le marqueur uniquement). */
export function resetDemoHospitolRich(): void {
  removeStorageItem(SEED_KEY);
  removeStorageItem(TREASURY_SEED_KEY);
}
